// Chemin: C:\gestion-copro\src\types\interlocuteur.types.ts
// Session 151 : Ajout types conseil_syndical, collectivite, coproprietaire, notaire
//               Champ notes neutralisé (plus "notes internes du CS" → "notes sur cet interlocuteur")
// Session 160 : Ajout FONCTIONS_PAR_TYPE — dropdown hybride fonction par type d'interlocuteur
// Session 174 : Ajout type 'locataire' comme interlocuteur à part entière (plus caché dans coproprietaire)

import { Timestamp } from 'firebase/firestore';

/**
 * Types d'interlocuteurs possibles
 * Session 151 : enrichi pour couvrir TOUT interlocuteur d'une copropriété
 * Session 174 : ajout 'locataire' — le locataire est un interlocuteur distinct, pas un copropriétaire
 */
export type TypeInterlocuteur = 
  | 'syndic' 
  | 'promoteur' 
  | 'constructeur' 
  | 'prestataire' 
  | 'assurance' 
  | 'avocat'
  | 'notaire'
  | 'expert' 
  | 'conseil_syndical'
  | 'collectivite'
  | 'coproprietaire'
  | 'locataire'
  | 'autre';

/**
 * Labels lisibles pour l'affichage dans l'interface
 */
export const TYPE_INTERLOCUTEUR_LABELS: Record<TypeInterlocuteur, string> = {
  syndic: 'Syndic',
  promoteur: 'Promoteur',
  constructeur: 'Constructeur',
  prestataire: 'Prestataire',
  assurance: 'Assurance',
  avocat: 'Avocat',
  notaire: 'Notaire',
  expert: 'Expert',
  conseil_syndical: 'Conseil syndical',
  collectivite: 'Collectivité',
  coproprietaire: 'Copropriétaire',
  locataire: 'Locataire',
  autre: 'Autre',
};

/**
 * Fonctions prédéfinies par type d'interlocuteur
 * Session 160 : Dropdown hybride — proposer des fonctions pertinentes selon le type
 * L'option "Autre" est gérée côté composant (pas dans cette liste)
 * Le type "autre" n'a pas de fonctions prédéfinies (saisie libre uniquement)
 * Session 174 : 'locataire' a ses propres fonctions, retiré de 'coproprietaire'
 */
export const FONCTIONS_PAR_TYPE: Record<TypeInterlocuteur, string[]> = {
  syndic: ['Gestionnaire', 'Directeur d\'agence', 'Comptable', 'Assistant(e)'],
  promoteur: ['Responsable SAV', 'Directeur de programme', 'Chargé de clientèle', 'Responsable technique'],
  constructeur: ['Conducteur de travaux', 'Chef de chantier', 'Directeur technique', 'Responsable SAV'],
  prestataire: ['Gérant', 'Technicien', 'Responsable', 'Chargé d\'affaires'],
  assurance: ['Gestionnaire sinistre', 'Agent', 'Courtier', 'Chargé de clientèle'],
  avocat: ['Associé', 'Collaborateur', 'Secrétaire juridique'],
  notaire: ['Notaire', 'Clerc de notaire', 'Assistant(e)'],
  expert: ['Expert judiciaire', 'Expert d\'assuré', 'Diagnostiqueur', 'Thermicien'],
  conseil_syndical: ['Président', 'Vice-président', 'Secrétaire', 'Trésorier', 'Membre'],
  collectivite: ['Maire', 'Adjoint à l\'urbanisme', 'Directeur des services techniques', 'Agent'],
  coproprietaire: ['Propriétaire occupant', 'Propriétaire bailleur'],
  locataire: ['Titulaire du bail', 'Co-titulaire'],
  autre: [],
};

/**
 * Civilité d'un contact (personne physique)
 * Session 174 : ajouté pour personnaliser l'appellation dans les courriers
 */
export type Civilite = 'M.' | 'Mme' | '';

/**
 * Contact au sein d'un interlocuteur
 * Un interlocuteur peut avoir plusieurs contacts (ex: gestionnaire + assistant)
 */
export interface Contact {
  nom: string;
  prenom?: string;
  civilite?: Civilite; // Session 174 : M. / Mme pour appellation courrier
  fonction?: string;
  email?: string;
  telephone?: string;
  principal: boolean; // Un seul contact principal par interlocuteur
}

/**
 * Informations contractuelles (optionnelles)
 * Utile pour les syndics, prestataires avec contrat
 */
export interface ContratInterlocuteur {
  dateDebut?: Timestamp;
  dateFin?: Timestamp;
  typeContrat?: string;
  montantAnnuel?: number;
}

/**
 * Entité principale Interlocuteur
 * Représente un tiers en relation avec la copropriété
 */
export interface Interlocuteur {
  id: string;
  coproprieteId: string; // Clé de cloisonnement multi-tenant
  
  type: TypeInterlocuteur;
  raisonSociale: string;
  siret?: string;
  
  adresse?: {
    rue: string;
    codePostal: string;
    ville: string;
  };
  
  contacts: Contact[];
  contrat?: ContratInterlocuteur;
  
  actif: boolean; // Permet de désactiver sans supprimer
  notes?: string; // Notes sur cet interlocuteur
  
  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Données pour la création d'un nouvel interlocuteur
 */
export interface InterlocuteurCreateData {
  coproprieteId: string;
  type: TypeInterlocuteur;
  raisonSociale: string;
  siret?: string;
  adresse?: {
    rue: string;
    codePostal: string;
    ville: string;
  };
  contacts: Contact[];
  contrat?: {
    dateDebut?: Date;
    dateFin?: Date;
    typeContrat?: string;
    montantAnnuel?: number;
  };
  actif?: boolean; // Par défaut true
  notes?: string;
}

/**
 * Données pour la mise à jour d'un interlocuteur
 */
export interface InterlocuteurUpdateData {
  type?: TypeInterlocuteur;
  raisonSociale?: string;
  siret?: string;
  adresse?: {
    rue?: string;
    codePostal?: string;
    ville?: string;
  };
  contacts?: Contact[];
  contrat?: Partial<ContratInterlocuteur>;
  actif?: boolean;
  notes?: string;
}

/**
 * Interlocuteur avec le contact principal extrait
 * Utile pour l'affichage dans les listes
 */
export interface InterlocuteurAvecContactPrincipal extends Interlocuteur {
  contactPrincipal?: Contact;
}
